/**
 * 인앱 링크 라우터 — <a> 클릭을 앱 화면 열기로 대체한다(규약 3.4 openSimpleBrowser).
 *
 * 앱이 웹뷰의 링크 가로채기를 제거하면서 라우팅 소유자가 웹으로 넘어왔다. 웹이 클릭을 가로채
 * 어떤 화면으로 열지 정한다.
 *
 * 정책: 내부 주소는 push(네이티브 바 없음 · 탭바 유지), 외부 주소는 modal(네이티브 상단 바 · X).
 *   외부 지면은 자기 헤더를 그리지 않으므로 push 하면 헤더도 나갈 버튼도 없는 화면이 된다.
 *   딥링크(piyonna:// 등 http(s) 아닌 스킴)와 결제 · 인증 지면은 가로채지 않는다.
 *
 * 앱 밖으로 내보내야 하는 링크는 data-app-link="external" 로 표시한다(규약 3.7 openExternalBrowser).
 *   기본 브라우저 · 다른 앱으로 넘기므로 앱 화면은 그대로 남는다. 표시 방식(push · modal)과 달리
 *   「어느 화면에 띄울지」가 아니라 「앱 밖으로 넘길지」라 스킴 제한을 받지 않는다.
 *
 * 예외: 링크가 data-app-link="modal"|"push" 로 표시 방식을 직접 지정하면 그 값이 이긴다.
 *   호스트 · 경로로는 알 수 없는 사실(「이 지면은 자기 헤더를 못 그린다」)을 링크가 아는 경우가
 *   있다. 신규 고객계정 주문내역(/account/orders)이 그렇다 — 경로는 내부인데 실제 지면은
 *   Shopify 호스팅이라 헤더가 없어, 제외 목록을 넘어 modal 로 열고 타이틀을 줘야 한다.
 *
 * 설계: docs/superpowers/specs/2026-08-19-in-app-link-router-design.md
 * 라우팅 규칙: docs/IN-APP.md 「링크 라우팅」 · 규약: docs/APP-BRIDGE.md 3.4
 *
 * 불변 규칙: 브릿지 호출이 성공한 뒤에만 preventDefault 한다.
 *   전송이 실패하면 브라우저가 그대로 이동하므로 링크가 죽는 경로가 없다.
 *
 * 이 파일은 라우팅 정책만 안다. 플랫폼 분기는 app-bridge.js 가 안다.
 */
(function () {
  'use strict';

  var app = window.PiyonnaApp;

  // 내부로 볼 호스트. 현재 origin(프리뷰 · 베타 테마의 *.myshopify.com)은 항상 내부다.
  var INTERNAL_HOSTS = ['piyonna.com', 'www.piyonna.com'];

  // 결제 호스트 — Shop Pay. modal 로 감싸면 결제 흐름이 끊긴다.
  var SKIP_HOSTS = ['shop.app'];

  // 앱이 시트 · 네이티브 흐름으로 직접 처리하는 경로.
  // locale-link-rewrite.js 의 EXCLUDED_PREFIXES + NO_LOCALE_PREFIX_PATHS 를 합친 목록이다
  // (그쪽은 로케일 프리픽스를 붙이지 않을 경로, 여기는 가로채지 않을 경로 — 대상이 같다).
  var SKIP_PATH_PREFIXES = [
    '/checkout',
    '/checkouts/',
    '/account',
    '/customer_authentication',
    '/apps/',
    '/services/',
    '/tools/',
    '/cdn/',
    '/.well-known/',
    '/admin/',
    '/wpm@',
  ];

  // 로케일 프리픽스 모양 — 'fr' · 'pt-br'. __SHOP_LOCALES__ 가 없을 때만 쓴다.
  var LOCALE_SHAPE = /^[a-z]{2}(-[a-z]{2})?$/;

  // 링크가 data-app-link 로 지정할 수 있는 표시 방식. 'off'(가로채지 않기)는 eligibility 가 본다.
  // 'external'(앱 밖으로 넘기기)은 표시 방식이 아니라 목적지가 달라 이 목록에 넣지 않는다.
  var PRESENTATIONS = ['push', 'modal'];

  // data-app-link="external" — 앱 밖으로 넘긴다(규약 3.7). 스킴을 가리지 않는다.
  var EXTERNAL = 'external';

  function skip(reason) {
    return { action: 'skip', reason: reason, url: '' };
  }

  function stripHash(href) {
    var index = href.indexOf('#');
    return index === -1 ? href : href.slice(0, index);
  }

  function localeCodes() {
    var list = window.__SHOP_LOCALES__; // scripts.liquid 가 주입 (locale-link-rewrite.js 와 공유)
    if (!Array.isArray(list) || list.length === 0) return null;

    return list.map(function (code) {
      return String(code).toLowerCase();
    });
  }

  /**
   * /fr/account 처럼 프리픽스가 붙은 경로도 제외 목록에 걸리게 한다.
   *
   * 프랑스어 지면의 routes.account_url 은 /fr/account 로 나오므로 스트립이 빠지면 계정 지면이
   * 내부 주소로 잡혀 심플 브라우저에 갇힌다. 주입값이 없을 때 스트립을 포기하지 않고 모양으로
   * 판단하는 이유다 — 과하게 벗겨도 결과는 「가로채지 않는다」쪽으로만 기울어 안전하다.
   */
  function stripLocalePrefix(pathname) {
    var codes = localeCodes();
    var segmentEnd = pathname.indexOf('/', 1);
    var segment = (segmentEnd === -1 ? pathname.slice(1) : pathname.slice(1, segmentEnd)).toLowerCase();
    if (!segment) return pathname;

    var isLocale = codes ? codes.indexOf(segment) !== -1 : LOCALE_SHAPE.test(segment);
    if (!isLocale) return pathname;

    return segmentEnd === -1 ? '/' : pathname.slice(segmentEnd);
  }

  /**
   * 링크가 지정한 표시 방식. 가장 가까운 [data-app-link] 값을 그대로 돌려준다 —
   * 'off' 판정은 eligibility 가 따로 하므로 여기서는 걸러내지 않는다.
   * @param {HTMLAnchorElement} anchor
   * @returns {string} 'push' · 'modal' · 'external' · 'off' · '' (지정 없음) · 알 수 없는 값
   */
  function presentation(anchor) {
    var owner = anchor.closest('[data-app-link]');
    return owner ? (owner.getAttribute('data-app-link') || '').trim().toLowerCase() : '';
  }

  /**
   * 링크 주소 판정. DOM 을 보지 않는 순수 함수 — 자격 심사는 호출하는 쪽이 한다.
   * @param {string} href
   * @param {string} [given] - 링크가 지정한 표시 방식(presentation()). 주면 정책보다 이긴다
   * @returns {{action: 'push'|'modal'|'external'|'skip', reason: string, url: string}}
   */
  function classify(href, given) {
    if (!href) return skip('href 없음');

    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (e) {
      return skip('URL 파싱 실패');
    }

    // 이동이 아닌 클릭은 지정 유무와 무관하게 결과가 같다 — 스킴 가드보다 먼저 본다.
    if (url.hash && stripHash(url.href) === stripHash(window.location.href)) {
      return skip('같은 문서 프래그먼트');
    }

    // 앱 밖으로 넘기라는 지정은 스킴 가드를 넘는다 — openExternalBrowser 는 앱 전환 딥링크까지
    // 받는다(규약 3.7). 아래 http(s) 가드는 openSimpleBrowser 의 pageUrl 제약이라 대상이 다르다.
    if (given === EXTERNAL) {
      return { action: EXTERNAL, reason: '링크 지정 (data-app-link="external")', url: url.href };
    }

    var protocol = url.protocol.toLowerCase();
    if (protocol !== 'http:' && protocol !== 'https:') {
      // piyonna:// 딥링크 · mailto: · tel: — pageUrl 은 http(s) 절대 주소만 허용한다(규약 3.4)
      // 앱 밖으로 넘겨야 하는 주소라면 링크가 data-app-link="external" 로 지정한다(위)
      return skip('http(s) 아닌 스킴: ' + protocol);
    }

    // 링크 지정이 정책을 이긴다. 위의 가드(스킴 · 프래그먼트)는 넘지 못한다 — 앱이 받지 못하는
    // 주소이거나 이동이 아니어서, 지정 유무와 무관하게 결과가 같다.
    if (given && given !== 'off') {
      if (PRESENTATIONS.indexOf(given) === -1) {
        return skip('알 수 없는 data-app-link 값: ' + given);
      }
      return { action: given, reason: '링크 지정 (data-app-link="' + given + '")', url: url.href };
    }

    var host = url.host.toLowerCase();
    if (SKIP_HOSTS.indexOf(host) !== -1) return skip('앱이 처리하는 호스트: ' + host);

    var isInternal = url.origin === window.location.origin || INTERNAL_HOSTS.indexOf(host) !== -1;

    if (isInternal) {
      var pathname = stripLocalePrefix(url.pathname);
      for (var i = 0; i < SKIP_PATH_PREFIXES.length; i++) {
        if (pathname.indexOf(SKIP_PATH_PREFIXES[i]) === 0) {
          return skip('앱이 처리하는 경로: ' + SKIP_PATH_PREFIXES[i]);
        }
      }

      // 탭 루트 판별은 서버 app_launch_config.tab_list 소유라 웹이 알 수 없다(규약 3.1).
      // 앱이 주입해 주면 여기서 selectTab(규약 3.5)으로 갈린다 — 그때까지는 전부 push.
      return { action: 'push', reason: '내부 주소', url: url.href };
    }

    return { action: 'modal', reason: '외부 주소', url: url.href };
  }

  /**
   * 가로챌 자격이 있는 클릭인가. 주소가 아니라 이벤트 · 엘리먼트를 본다.
   * 채널이 없어도 노출한다 — 테스트 지면이 「판정(주소)」과 「자격」을 나눠 보여줄 수 있게.
   * @param {MouseEvent} event
   * @param {HTMLAnchorElement} anchor
   * @returns {{eligible: boolean, reason: string}}
   */
  function eligibility(event, anchor) {
    if (event.defaultPrevented) return { eligible: false, reason: '테마 컴포넌트가 이미 처리' };
    if (event.button !== 0) return { eligible: false, reason: '좌클릭 아님' };
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return { eligible: false, reason: '보조키 눌림 (새 탭 등)' };
    }
    if (anchor.hasAttribute('download')) return { eligible: false, reason: '다운로드 링크' };

    var target = (anchor.getAttribute('target') || '').trim().toLowerCase();
    // _blank 등은 앱의 새 창 처리에 맡긴다
    if (target && target !== '_self') return { eligible: false, reason: 'target=' + target };

    if (anchor.closest('[data-app-link="off"]')) return { eligible: false, reason: '옵트아웃 (data-app-link="off")' };

    return { eligible: true, reason: '' };
  }

  // 판정 로직은 채널이 없어도 노출한다 — 테스트 지면 · Node 하네스용
  window.PiyonnaAppLinkRouter = { classify: classify, eligibility: eligibility, presentation: presentation };

  // 일반 브라우저에서는 리스너를 달지 않는다 — 채널 존재로 판단한다(규약 1.6).
  // <html>.in-app 클래스나 ?in-app=1 은 채널을 만들지 않으므로 게이팅 기준이 될 수 없다.
  if (!app || !app.isAvailable()) return;

  // 캡처가 아니라 버블이다. locale-link-rewrite.js 가 캡처 단계에서 /fr 프리픽스를 붙이므로
  // 최종 href 는 버블에서만 읽을 수 있고, 테마 컴포넌트가 이미 처리한 링크는
  // event.defaultPrevented 로 걸러진다. stopPropagation 은 쓰지 않는다 — 다른 버블 리스너를
  // 죽인다. preventDefault 만으로 충분하다.
  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    var anchor = target ? target.closest('a[href]') : null;
    if (!anchor || !eligibility(event, anchor).eligible) return;

    var verdict = classify(anchor.href, presentation(anchor));
    if (verdict.action === 'skip') return;

    // 앱 밖으로 넘기는 경로는 화면을 띄우지 않으므로 표시 옵션이 없다(규약 3.7)
    if (verdict.action === EXTERNAL) {
      // 호출이 성공한 뒤에만 기본 이동을 막는다 — 실패하면 브라우저가 그대로 이동한다
      if (app.openExternalBrowser(verdict.url)) event.preventDefault();
      return;
    }

    var options = {};
    if (verdict.action === 'modal') {
      options.presentationType = 'modal';
      // 기본은 지면 <title> 을 따라간다(규약 3.4). 고정이 필요한 링크만 속성으로 덮어쓴다
      var title = anchor.getAttribute('data-app-link-title');
      if (title) options.title = title;
    }

    // 호출이 성공한 뒤에만 기본 이동을 막는다 — 실패하면 브라우저가 그대로 이동한다
    if (app.openSimpleBrowser(verdict.url, options)) event.preventDefault();
  });
})();
